import React from "react"
import { useState, useEffect } from "react"
import { Button } from "../../../components/ui/button"
import { Input } from "../../../components/ui/input"
import { Label } from "../../../components/ui/label"
import { Textarea } from "../../../components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select"
import { Building2, Plus, X, Loader2 } from "lucide-react"
import { MOCK_HIERARCHY_DATA, MOCK_FIELD_TYPES } from "./Mock-Data"
import type { HierarchyNode, FieldType, MetaDataField } from "./Types"
import DynamicFieldForm from "./DynamicsField"
import { Sheet, SheetClose, SheetContent, SheetHeader, SheetTitle } from "../../../components/ui/sheet"
import { useProductDesignManagement } from "../../Services/ProductDesignManagement"
import { useCategoryManagement } from "../../Services/CategoryManagement"
import { toast } from "react-toastify"
import ConfirmDialog from "../Modals/ConfirmationModal"
import { isApiError, getApiErrorMessage } from "../../../Utilities/apiResponse"

interface ProductTypeDrawerProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    onSuccess?: () => void
}

export const ProductType = () => {
    const [fieldTypes, setFieldTypes] = useState<FieldType[]>([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const { getMetaDataFieldTypes, getTenantHierarchy, createMetaDataField, updateMetaDataField, deleteMetaDataField } = useProductDesignManagement();
    const { getCategories } = useCategoryManagement();
    const [categories, setCategories] = useState<{ id: string; categoryName: string }[]>([]);

    // Form state
    const [selectedNode1, setSelectedNode1] = useState("")
    const [selectedNode2, setSelectedNode2] = useState("")
    const [selectedNode3, setSelectedNode3] = useState("")
    const [selectedNode4, setSelectedNode4] = useState("")
    const [fields, setFields] = useState<MetaDataField[]>([])
    const [updatedfields, setUpdatedFields] = useState<MetaDataField[]>([])

    // Derived state for cascading dropdowns
    const [node2Options, setNode2Options] = useState<HierarchyNode[]>([])
    const [node3Options, setNode3Options] = useState<HierarchyNode[]>([])
    const [node4Options, setNode4Options] = useState<HierarchyNode[]>([])

    const [hierarchyData, setHierarchyData] = useState<HierarchyNode[]>([]);
    const [hierarchyLoading, setHierarchyLoading] = useState(true);
    const [fieldErrors, setFieldErrors] = useState<Record<number, { fieldName?: boolean; fieldDisplayName?: boolean; masterMetaDataFieldTypeId?: boolean }>>({});

    const [confirmationModal, setConfirmationModal] = useState({
        isShow: false,
        onConfirm: () => { },
    });

    // const hierarchyData = MOCK_HIERARCHY_DATA

    useEffect(() => {
        loadInitialData();
    }, []);

    const loadInitialData = () => {
        setHierarchyLoading(true);
        getTenantHierarchy().then((data) => {
            setHierarchyData(data);
        }).catch(() => {
            setHierarchyData([]);
        }).finally(() => {
            setHierarchyLoading(false);
        })

        getMetaDataFieldTypes().then((data) => {
            setFieldTypes(data);
        }).catch(() => {
            setFieldTypes([]);
        });

        getCategories().then((data) => {
            if (Array.isArray(data)) {
                setCategories(data);
            }
        }).catch(() => {
            setCategories([]);
        });
    }

    // Update node2 options when node1 changes
    useEffect(() => {
        if (selectedNode1) {
            const node1 = hierarchyData.find((n) => n.id === selectedNode1)
            // @ts-ignore
            setNode2Options(node1?.children || [])
            setSelectedNode2("")
            setSelectedNode3("")
            setSelectedNode4("")
        } else {
            setNode2Options([])
        }
    }, [selectedNode1])

    // Update node3 options when node2 changes
    useEffect(() => {
        if (selectedNode2) {
            const node2 = node2Options.find((n) => n.id === selectedNode2)
            setNode3Options(node2?.children || [])
            setSelectedNode3("")
            setSelectedNode4("")
        } else {
            setNode3Options([])
        }
    }, [selectedNode2, node2Options])

    // Update node4 options when node3 changes
    useEffect(() => {
        if (selectedNode3) {
            const node3 = node3Options.find((n) => n.id === selectedNode3)
            setNode4Options(node3?.children || [])
            setSelectedNode4("")
        } else {
            setNode4Options([])
        }
    }, [selectedNode3, node3Options])

    const handleAddField = () => {
        const newField: MetaDataField = {
            id: "",
            tenantHierarchyNodeId: selectedNode3 || selectedNode4 || "",
            fieldName: "",
            fieldDisplayName: "",
            fieldDescription: "",
            masterMetaDataFieldTypeId: "",
            isRequiredField: false,
            isSendToBlockChain: false,
            fieldValue: "",
            isSystemField: false,
            fieldData: [],
            masterMetaDataFieldType: {
                id: "",
                fieldType: "",
                backendFieldType: "",
                frontEndFieldType: "",
                icon: "",
            },
        }
        setFields([...fields, newField])
    }

    const handleDeleteField = (index, field) => {
        handleRemoveField(index);
        if (field.id !== "") {
            deleteMetaDataField(field.id)
                .then((response) => {
                    if (isApiError(response)) {
                        toast.error("Product Type Field deleting failed");
                        loadInitialData();
                        return;
                    }
                    toast.success("Product Type Field Deleted");
                    loadInitialData();
                })
                .catch(() => {
                    toast.error("Product Type Field deleting failed");
                })
        }
    }

    const handleRemoveField = (index: number) => {
        const updatedFields = fields.filter((_, i) => i !== index)
        setFields(updatedFields)
    }

    const handleFieldChange = (index: number, updatedField: MetaDataField) => {
        const updatedFields = [...fields]
        updatedFields[index] = updatedField
        if (updatedField.id !== "") {
            setUpdatedFields((prev) => {
                const index = prev.findIndex(f => f.id === updatedField.id);
                console.log("index : ", index);
                if (index !== -1) {
                    // update existing one
                    const copy = [...prev];
                    copy[index] = { ...copy[index], ...updatedField };
                    return copy;
                }

                // add new one
                return [...prev, updatedField];
            });
        }
        setFields(updatedFields)
    }

    const nodeIndex = React.useMemo(() => {
        const index = new Map<string, any>();
        const traverse = (nodes: any[]) => {
            for (const node of nodes) {
                index.set(node.id, node);
                if (node.children) traverse(node.children);
            }
        };
        traverse(hierarchyData);
        return index;
    }, [hierarchyData]);

    const handleNode3Data = (nodeId: string) => {
        const node3 = nodeIndex.get(nodeId);
        setFields(node3?.metaDataFields || [])
    }

    const validateBeforeSubmit = (newFields: MetaDataField[], updatedFieldsList: MetaDataField[]) => {
        if (newFields.length === 0 && updatedFieldsList.length === 0) {
            return "No changes to save";
        }

        if (!selectedNode3 && !selectedNode4) {
            return "Please select a Product Type or Product Design level before saving";
        }

        const errors: Record<number, { fieldName?: boolean; fieldDisplayName?: boolean; masterMetaDataFieldTypeId?: boolean }> = {};
        const allFields = [...newFields, ...updatedFieldsList];

        for (let i = 0; i < allFields.length; i++) {
            const field = allFields[i];
            const fieldIndex = fields.findIndex((f) => f === field || (f.id && f.id === field.id));
            const index = fieldIndex >= 0 ? fieldIndex : i;

            if (!field.fieldName?.trim()) {
                errors[index] = { ...errors[index], fieldName: true };
            }
            if (!field.fieldDisplayName?.trim()) {
                errors[index] = { ...errors[index], fieldDisplayName: true };
            }
            if (!field.masterMetaDataFieldTypeId?.trim()) {
                errors[index] = { ...errors[index], masterMetaDataFieldTypeId: true };
            }
            if (!field.tenantHierarchyNodeId?.trim()) {
                errors[index] = { ...errors[index], fieldName: true };
            }
        }

        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            return "Please fill in all required field properties before saving";
        }

        setFieldErrors({});
        return null;
    }

    const handleSubmit = async () => {
        const newFields = fields.filter(item => item.id === "");
        const validationError = validateBeforeSubmit(newFields, updatedfields);

        if (validationError) {
            setError(validationError);
            if (validationError !== "No changes to save") {
                toast.error(validationError);
            }
            return;
        }

        setLoading(true);
        setError(null);

        const requests: Promise<unknown>[] = [];

        if (updatedfields.length > 0) {
            updatedfields.forEach(field => {
                const modifiedField = { ...field };
                // @ts-ignore
                delete modifiedField.masterMetaDataFieldType;
                requests.push(updateMetaDataField(modifiedField));
            });
        }

        if (newFields.length > 0) {
            newFields.forEach(field => {
                const modifiedField = { ...field };
                // @ts-ignore
                delete modifiedField.id;
                // @ts-ignore
                delete modifiedField.masterMetaDataFieldType;
                requests.push(createMetaDataField(modifiedField));
            });
        }

        try {
            const results = await Promise.allSettled(requests);
            const failures = results.filter(
                (result) => result.status === "rejected" || (result.status === "fulfilled" && isApiError(result.value))
            );

            if (failures.length > 0) {
                const firstError = failures.find(
                    (result) => result.status === "fulfilled" && isApiError(result.value)
                );
                const message = firstError && firstError.status === "fulfilled"
                    ? getApiErrorMessage(firstError.value)
                    : "Failed to save product type field(s)";
                toast.error(message);
                setError(message);
                return;
            }

            toast.success("Product type saved successfully");
            resetForm();
            loadInitialData();
            setUpdatedFields([]);
            setFieldErrors({});
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to create or update product type");
            toast.error("Failed to save product type field(s)");
        } finally {
            setLoading(false);
        }
    }

    const resetForm = () => {
        setSelectedNode1("")
        setSelectedNode2("")
        setSelectedNode3("")
        setSelectedNode4("")
        setFields([])
        setError(null)
        setFieldErrors({})
    }

    // Get node1 options (nodeType === 1)
    const node1Options = hierarchyData.filter((n) => n.nodeType === 1)

    return (
        <div className="w-full mx-auto p-6 space-y-6 product-types">
            <div className="container mx-auto p-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                    <h1 className="text-2xl font-bold text-gray-900">Manage Product Types</h1>
                    {(fields?.filter(item => item.id === "").length > 0 || updatedfields?.length > 0) && (
                        <Button
                            className="bg-purple-600 hover:bg-purple-700 transition-colors"
                            onClick={handleSubmit}
                        >
                            <Plus className="h-4 w-4 mr-2" />
                            Save Changes
                        </Button>
                    )}
                </div>

                <div className="flex-1 overflow-y-auto">
                    {error && (
                        <div className="mb-4 p-3 bg-red-950 border border-red-800 rounded text-red-200 text-sm">{error}</div>
                    )}

                    {/* Hierarchy Dropdowns */}
                    <div className="space-y-4 mb-6 rounded-lg">
                        <div>
                            <Label htmlFor="node1" className="text-sm font-medium text-slate-200">
                                Category
                            </Label>
                            {hierarchyLoading ? (
                                <div className="flex items-center gap-2 mt-2 text-slate-400 text-sm">
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Loading product types...
                                </div>
                            ) : (
                            <Select value={selectedNode1} onValueChange={setSelectedNode1} disabled={loading}>
                                <SelectTrigger id="node1" className="w-full mt-2">
                                    <SelectValue placeholder="Select Level 1" />
                                </SelectTrigger>
                                <SelectContent className="w-full">
                                    {node1Options.map((node) => (
                                        <SelectItem key={node.id} value={node.id} className="text-white">
                                            {node.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            )}
                        </div>

                        {selectedNode1 && node2Options.length > 0 && (
                            <div>
                                <Label htmlFor="node2" className="text-sm font-medium text-slate-200">
                                    Sub Category
                                </Label>
                                <Select value={selectedNode2} onValueChange={setSelectedNode2} disabled={loading}>
                                    <SelectTrigger id="node2" className="w-full mt-2">
                                        <SelectValue placeholder="Select Level 2" />
                                    </SelectTrigger>
                                    <SelectContent className="w-full">
                                        {node2Options.map((node) => (
                                            <SelectItem key={node.id} value={node.id} className="text-white">
                                                {node.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        {selectedNode2 && node3Options.length > 0 && (
                            <div>
                                <Label htmlFor="node3" className="text-sm font-medium text-slate-200">
                                    Product Type
                                </Label>
                                <Select
                                    value={selectedNode3}
                                    onValueChange={(event) => {
                                        setSelectedNode3(event);
                                        handleNode3Data(event);
                                    }}
                                    disabled={loading}
                                >
                                    <SelectTrigger id="node3" className="w-full mt-2">
                                        <SelectValue placeholder="Select Level 3" />
                                    </SelectTrigger>
                                    <SelectContent className="w-full">
                                        {node3Options.map((node) => (
                                            <SelectItem key={node.id} value={node.id} className="text-white">
                                                {node.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        {selectedNode3 && node4Options.length > 0 && (
                            <div>
                                <Label htmlFor="node4" className="text-sm font-medium text-slate-200">
                                    Product Design
                                </Label>
                                <Select value={selectedNode4} onValueChange={setSelectedNode4} disabled={loading}>
                                    <SelectTrigger id="node4" className="w-full">
                                        <SelectValue placeholder="Select Level 4" />
                                    </SelectTrigger>
                                    <SelectContent className="w-full">
                                        {node4Options.map((node) => (
                                            <SelectItem key={node.id} value={node.id} className="text-white">
                                                {node.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                    </div>

                    {/* Dynamic Fields Section - Only show at nodeType 3 */}
                    {(selectedNode3 || selectedNode4) && (
                        <div className="space-y-4 mb-6">
                            <div className="flex items-center justify-between">
                                <h3 className="font-semibold text-sm text-white">Dynamic Fields</h3>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={handleAddField}
                                    disabled={loading}
                                    className="gap-1"
                                >
                                    <Plus className="w-4 h-4" />
                                    Add Field
                                </Button>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {fields.map((field, index) => (
                                    <div key={index} className="p-4 border border-slate-600 rounded-lg relative">
                                        {!field.isSystemField && (
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => {
                                                    setConfirmationModal({
                                                        isShow: true,
                                                        onConfirm: () => handleDeleteField(index, field),
                                                    })
                                                }}
                                                disabled={field.isSystemField}
                                                className="absolute top-2 right-2"
                                            >
                                                <X className="w-4 h-4" />
                                            </Button>
                                        )}

                                        <DynamicFieldForm
                                            field={field}
                                            fieldTypes={fieldTypes}
                                            categories={categories}
                                            onChange={(updated) => {
                                                handleFieldChange(index, updated);
                                                setFieldErrors((prev) => {
                                                    const next = { ...prev };
                                                    delete next[index];
                                                    return next;
                                                });
                                            }}
                                            disabled={field.isSystemField}
                                            isShowValues={true}
                                            errorFields={fieldErrors[index]}
                                        />
                                    </div>
                                ))}

                                {fields.length === 0 && (
                                    <div className="p-4 text-center text-slate-400 text-sm border border-dashed border-slate-600 rounded-lg">
                                        No fields added yet. Click "Add Field" to create one.
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <ConfirmDialog isShow={confirmationModal?.isShow} setIsDialogOpen={setConfirmationModal} title="Are you sure to remvoe this field ?" description="" confirmText="Delete" cancelText="Cancel" onConfirm={confirmationModal?.onConfirm} />
        </div>
    )
}
